import { Alert, Box } from '@mui/material'
import { useLayoutEffect, useRef } from 'react'
import { useOrganizationUnsafe } from '../OrganizationProvider'

// Exposed so fixed-position chrome (AppDrawer) can offset itself below the banner.
export const OUTAGE_BANNER_HEIGHT_VAR = '--outage-banner-height'

/**
 * Site-wide outage notice sourced from GetConfig's `outageBanner` field.
 * Renders nothing when the value is null/undefined/blank. Not dismissible by design:
 * it should reappear on every load while the value is present.
 */
export function OutageBanner() {
  const message = useOrganizationUnsafe()?.outageBanner?.trim() || ''
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const root = document.documentElement
    const el = ref.current
    if (!message || !el) {
      root.style.removeProperty(OUTAGE_BANNER_HEIGHT_VAR)
      return
    }

    const update = () => root.style.setProperty(OUTAGE_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`)
    update()

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    observer?.observe(el)
    window.addEventListener('resize', update)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', update)
      root.style.removeProperty(OUTAGE_BANNER_HEIGHT_VAR)
    }
  }, [message])

  if (!message) return null

  return (
    <Box
      ref={ref}
      sx={{
        position: 'sticky',
        top: 0,
        width: '100%',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Alert
        severity="warning"
        role="status"
        aria-live="polite"
        sx={{
          borderRadius: 0,
          justifyContent: 'center',
          alignItems: 'center',
          py: 0.5,
          px: 2,
          bgcolor: 'warning.light',
          color: (theme) => theme.palette.getContrastText(theme.palette.warning.light),
          fontWeight: 500,
          '& .MuiAlert-icon': { color: 'inherit' },
          '& .MuiAlert-message': { overflowWrap: 'anywhere' },
        }}
      >
        {message}
      </Alert>
    </Box>
  )
}
